import {
  looksLikeBoothAreaNumber,
  looksLikeBoothCode,
  looksLikeLetterBoothCode,
  normalizeBoothCode,
  parseBoothRectsFromSvg,
  parseBoothTextLabelsFromSvg,
  parseLabeledBoothRectsFromSvg,
  parseSvgBoardSize,
  type LabeledBoothRect,
  type UnderlaySvgLayerFlags,
} from "./floorplanSvg.js";

/** Semantic objects on our own pavilion schema (not Corel SVG underlay). */
export type FloorplanObjectKind =
  | "booth"
  | "entrance"
  | "exit"
  | "stairs"
  | "toilet"
  | "info"
  | "service"
  | "zone"
  | "other"
  /** Clickable plaque → sibling exhibition map (same hall / group). */
  | "event_link";

/** Arrow on an event_link plaque (screen-ish: e=→, w=←, n=↑, s=↓). */
export type FloorplanLinkArrow = "e" | "w" | "n" | "s";

export type FloorplanSceneObject = {
  id: string;
  kind: FloorplanObjectKind;
  /** Booth / object number, e.g. A301 */
  code?: string;
  /** Display label (Вход, Лестница…) */
  label?: string;
  /**
   * Target event id for `event_link` (sibling in the same exhibition group).
   * Visitor tap switches to that event's map.
   */
  linkEventId?: string;
  /** Direction of the transition arrow on `event_link`. Default `e`. */
  linkArrow?: FloorplanLinkArrow;
  /** Target exhibition primary color (#rrggbb) for `event_link` plaque styling. */
  linkAccent?: string;
  /**
   * Show this marker in visitor PWA / miniapp.
   * Default true for navigation markers (вход, туалет…); booths usually rely on pins.
   * `false` = admin-only helper on the scheme.
   */
  showInApp?: boolean;
  /** Admin editor: locked object can't be dragged/resized. */
  locked?: boolean;
  /** Label size multiplier for this object (0.5–2.5). Default 1. */
  fontScale?: number;
  /**
   * Extra label rotation for this object (0 / 90 / 180 / 270), on top of
   * board upright counter-rotate and scene.pinLabelRotationDeg.
   */
  labelRot?: 0 | 90 | 180 | 270;
  /** Axis-aligned box as % of board (0–100). */
  x: number;
  y: number;
  w: number;
  h: number;
};

export type FloorplanSceneSource = {
  format: "cdr" | "svg" | "manual";
  fileName?: string;
  importedAt: string;
  /** Codes found in Corel textinfo (may be richer than SVG labels). */
  boothCodesHint?: string[];
};

export type FloorplanScene = {
  version: 1;
  width: number;
  height: number;
  /** Whole-plan rotation applied after import (0 / 90 / 180 / 270). */
  rotationDeg?: 0 | 90 | 180 | 270;
  /**
   * When false, admin + visitor hide the Corel SVG underlay and show only
   * our pins / markers (underlay stays stored as edit reference).
   * Default: true (undefined = show underlay).
   */
  underlayVisible?: boolean;
  /**
   * Toggle slices inside the underlay SVG (grid / black contours / fills…).
   * Missing keys use defaults (contours off — they are usually LO noise).
   */
  underlayLayers?: UnderlaySvgLayerFlags;
  /** Placement (pin) label size multiplier (0.7–2.2). Default 1. */
  labelScale?: number;
  /**
   * Extra rotation for all placement pin labels (0 / 90 / 180 / 270).
   * Combined with per-booth mapLabelRot and optional upright counter-rotate.
   */
  pinLabelRotationDeg?: 0 | 90 | 180 | 270;
  /**
   * When true (default), pin labels counter-rotate against board rotation
   * so text stays screen-horizontal ("Надписи прямо").
   */
  labelsUpright?: boolean;
  /**
   * Empty margins (%) around the Corel underlay after «Поле ↑/+10%».
   * Pins / markers are remapped into the remaining content rect; underlay
   * is drawn inset so it stays aligned. Visitor map has no underlay — the
   * freed strip is just empty board for event_link plaques.
   */
  contentPad?: { left: number; top: number; right: number; bottom: number };
  objects: FloorplanSceneObject[];
  source?: FloorplanSceneSource;
};

const ARCH_RULES: Array<{ kind: FloorplanObjectKind; re: RegExp; label: string }> = [
  { kind: "stairs", re: /лестниц|stairs?|stair\b/i, label: "Лестница" },
  { kind: "exit", re: /выход|exit\b|эвакуац/i, label: "Выход" },
  // Corel often draws "ВХОД" in caps; LO may split spacing.
  { kind: "entrance", re: /в\s*х\s*о\s*д(?!ит)|entrance|enter\b/i, label: "Вход" },
  { kind: "toilet", re: /туалет|сануз|w\s*\/?\s*c|toilet|restroom/i, label: "Туалет" },
  { kind: "info", re: /инфо|справк|\binfo\b|reception|регистрац/i, label: "Инфо" },
  { kind: "service", re: /кафе|buffet|еда|food|бар\b|bar\b|кухн/i, label: "Сервис" },
  {
    kind: "zone",
    re: /конференц|conference|переговор|meeting/i,
    label: "Конференц-зал",
  },
  {
    kind: "zone",
    re: /офис\s*организатор|organi[sz]er.?s?\s*office|организатор/i,
    label: "Офис организаторов",
  },
];

function classifyArchLabel(text: string): { kind: FloorplanObjectKind; label: string } | null {
  const t = text.trim();
  if (!t || looksLikeBoothCode(t)) return null;
  for (const rule of ARCH_RULES) {
    if (rule.re.test(t)) return { kind: rule.kind, label: rule.label };
  }
  return null;
}

function round2(n: number): number {
  return Number(n.toFixed(2));
}

function newId(kind: string, i: number): string {
  return `${kind}-${i}-${Math.random().toString(36).slice(2, 7)}`;
}

/** All `<text>` plain contents with approximate % anchors (LibreOffice-aware). */
function parseAllTextAnchors(svg: string): Array<{ text: string; x: number; y: number }> {
  const s = String(svg ?? "");
  if (!s) return [];
  const vbMatch = s.match(/viewBox\s*=\s*["']([^"']+)["']/i);
  const parts = vbMatch?.[1]?.trim().split(/[\s,]+/).map(Number) ?? [];
  const vb = {
    x: parts[0] ?? 0,
    y: parts[1] ?? 0,
    w: parts[2] && parts[2]! > 0 ? parts[2]! : 1000,
    h: parts[3] && parts[3]! > 0 ? parts[3]! : 700,
  };
  const out: Array<{ text: string; x: number; y: number }> = [];
  for (const m of s.matchAll(/<text\b([^>]*)>([\s\S]*?)<\/text>/gi)) {
    const attrs = m[1] ?? "";
    const inner = m[2] ?? "";
    const plain = inner
      .replace(/<[^>]+>/g, " ")
      .replace(/&nbsp;/gi, " ")
      .replace(/&amp;/g, "&")
      .replace(/\s+/g, " ")
      .trim();
    if (!plain) continue;
    const idx = m.index ?? 0;
    const preceding = s.slice(Math.max(0, idx - 280), idx);
    const pos =
      inner.match(
        /TextPosition[^>]*\bx="([^"]+)"[^>]*\by="([^"]+)"|\by="([^"]+)"[^>]*\bx="([^"]+)"/i,
      ) ?? inner.match(/\bx="([^"]+)"[^>]*\by="([^"]+)"/i);
    let x = NaN;
    let y = NaN;
    if (pos) {
      x = Number(pos[1] ?? pos[4]);
      y = Number(pos[2] ?? pos[3]);
    }
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      const bb = preceding.match(
        /<rect[^>]*class="BoundingBox"[^>]*\bx="([^"]+)"[^>]*\by="([^"]+)"[^>]*\bwidth="([^"]+)"[^>]*\bheight="([^"]+)"/i,
      );
      if (bb) {
        x = Number(bb[1]) + Number(bb[3]) / 2;
        y = Number(bb[2]) + Number(bb[4]) / 2;
      }
    }
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      x = Number(attrs.match(/\bx="([^"]+)"/i)?.[1]);
      y = Number(attrs.match(/\by="([^"]+)"/i)?.[1]);
    }
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    if (Math.abs(x) < 1 && Math.abs(y) < 1) continue;
    out.push({
      text: plain,
      x: ((x - vb.x) / vb.w) * 100,
      y: ((y - vb.y) / vb.h) * 100,
    });
  }
  return out;
}

export type ExtractFloorplanSceneOptions = {
  format?: "cdr" | "svg" | "manual";
  fileName?: string;
  boothCodesHint?: string[];
  fill?: string;
};

/**
 * Empty overlay scene matching the SVG underlay page.
 * Upload keeps Corel 1:1 as underlay; markers are drawn manually in the scheme editor.
 */
export function createEmptyFloorplanScene(
  width: number,
  height: number,
  options?: ExtractFloorplanSceneOptions,
): FloorplanScene {
  return {
    version: 1,
    width: Math.max(200, Math.round(width) || 1000),
    height: Math.max(200, Math.round(height) || 700),
    rotationDeg: 0,
    underlayVisible: true,
    labelScale: 1,
    objects: [],
    source: {
      format: options?.format ?? "manual",
      fileName: options?.fileName,
      importedAt: new Date().toISOString(),
      boothCodesHint: options?.boothCodesHint,
    },
  };
}

/**
 * Build our semantic pavilion scene from a Corel/Illustrator SVG export
 * (or SVG produced from .cdr). SVG is a parse intermediate — not the product UI.
 */
export function extractFloorplanSceneFromSvg(
  svg: string,
  options?: ExtractFloorplanSceneOptions,
): FloorplanScene {
  const size = parseSvgBoardSize(svg);
  const labeled = parseLabeledBoothRectsFromSvg(svg, { fill: options?.fill });
  const unlabeledRects = parseBoothRectsFromSvg(svg, { fill: options?.fill });
  const boothLabels = parseBoothTextLabelsFromSvg(svg);
  const usedCenters = new Set(labeled.map((b) => `${b.cx.toFixed(1)},${b.cy.toFixed(1)}`));

  const objects: FloorplanSceneObject[] = [];
  let i = 0;

  for (const b of labeled) {
    const code = normalizeBoothCode(b.code);
    // Never promote Corel sq.m markers to booth IDs when A### exists on the plan.
    if (!looksLikeLetterBoothCode(code) && labeled.some((x) => looksLikeLetterBoothCode(x.code))) {
      continue;
    }
    objects.push({
      id: newId("booth", i++),
      kind: "booth",
      code,
      // label filled below with company name from SVG when present
      label: code,
      x: round2(b.x),
      y: round2(b.y),
      w: round2(Math.max(1.2, b.w)),
      h: round2(Math.max(1.2, b.h)),
    });
  }

  const letterBoothCount = objects.filter(
    (o) => o.kind === "booth" && o.code && looksLikeLetterBoothCode(o.code),
  ).length;

  // Coral cells without a paired A### label — only keep when the plan has few
  // letter booths (legacy numeric halls). Otherwise they become empty "phantom"
  // rectangles that do not exist as labeled stands on the Corel sheet.
  if (letterBoothCount < 5) {
    for (const r of unlabeledRects) {
      const key = `${r.cx.toFixed(1)},${r.cy.toFixed(1)}`;
      if (usedCenters.has(key)) continue;
      const covered = labeled.some(
        (b) => r.cx >= b.x && r.cx <= b.x + b.w && r.cy >= b.y && r.cy <= b.y + b.h,
      );
      if (covered) continue;
      usedCenters.add(key);
      objects.push({
        id: newId("booth", i++),
        kind: "booth",
        x: round2(r.x),
        y: round2(r.y),
        w: round2(Math.max(1.2, r.w)),
        h: round2(Math.max(1.2, r.h)),
      });
    }
  }

  // Orphan booth numbers (text without a coral cell) → small markers.
  // Skip when we already have a solid letter-booth plan — orphans are almost
  // always area numbers / duplicates near real cells.
  const codesOnPlan = new Set(
    objects.map((o) => o.code).filter(Boolean) as string[],
  );
  if (letterBoothCount < 8) {
    for (const lb of boothLabels) {
      const code = normalizeBoothCode(lb.code);
      if (!code || codesOnPlan.has(code)) continue;
      if (!looksLikeLetterBoothCode(code) && letterBoothCount > 0) continue;
      if (lb.x < 0.2 && lb.y < 0.2) continue;
      const nearExisting = objects.some((o) => {
        if (o.kind !== "booth") return false;
        const cx = o.x + o.w / 2;
        const cy = o.y + o.h / 2;
        return Math.hypot(lb.x - cx, lb.y - cy) < 4;
      });
      if (nearExisting) continue;
      codesOnPlan.add(code);
      objects.push({
        id: newId("booth", i++),
        kind: "booth",
        code,
        label: code,
        x: round2(Math.max(0, lb.x - 1.5)),
        y: round2(Math.max(0, lb.y - 1.2)),
        w: 3,
        h: 2.4,
      });
    }
  }

  // Company / zone titles drawn inside Corel cells (GENESIS, УГЛТУ…).
  const nameAnchors = parseAllTextAnchors(svg).filter((t) => {
    const s = t.text.trim();
    if (s.length < 2 || s.length > 64) return false;
    if (looksLikeBoothCode(s) || looksLikeBoothAreaNumber(s)) return false;
    if (classifyArchLabel(s)) return false;
    if (/^(павильон|pavilion|зал|hall|м²|м2|кв\.?\s*м)\b/i.test(s)) return false;
    return true;
  });
  for (const o of objects) {
    if (o.kind !== "booth") continue;
    const hits = nameAnchors
      .filter(
        (t) =>
          t.x >= o.x - 0.3 &&
          t.x <= o.x + o.w + 0.3 &&
          t.y >= o.y - 0.3 &&
          t.y <= o.y + o.h + 0.3,
      )
      .sort((a, b) => b.text.length - a.text.length);
    if (!hits.length) continue;
    // Collective stands: keep up to 4 shortest unique titles (readable on cell).
    const titles: string[] = [];
    for (const h of hits) {
      const t = h.text.replace(/\s+/g, " ").trim();
      if (!t || titles.some((x) => x.toLowerCase() === t.toLowerCase())) continue;
      titles.push(t);
      if (titles.length >= 4) break;
    }
    if (titles.length) o.label = titles.join(" · ");
  }

  // Architectural labels (ВХОД, W/C, конференц-зал…) — keep even outside booth cluster.
  const archSeen = new Set<string>();
  for (const t of parseAllTextAnchors(svg)) {
    const arch = classifyArchLabel(t.text);
    if (!arch) continue;
    const key = `${arch.kind}:${t.x.toFixed(0)},${t.y.toFixed(0)}`;
    if (archSeen.has(key)) continue;
    archSeen.add(key);
    const isZone = arch.kind === "zone";
    objects.push({
      id: newId(arch.kind, i++),
      kind: arch.kind,
      label: arch.label,
      x: round2(Math.max(0, t.x - (isZone ? 4 : 2.5))),
      y: round2(Math.max(0, t.y - (isZone ? 2.5 : 1.8))),
      w: isZone ? 8 : 5.5,
      h: isZone ? 5 : 4,
    });
  }

  // Do not invent geometry for CDR textinfo-only codes — they confuse the plan.
  // Numbers must come from SVG text paired with coral cells.

  // Keep only letter-coded booths (+ architecture). Drop bare numbers and
  // unlabeled coral phantoms when the pavilion uses A### stands.
  const hasLetter = objects.some((o) => o.kind === "booth" && o.code && /[A-Z]/.test(o.code));
  const cleaned = hasLetter
    ? objects.filter(
        (o) => o.kind !== "booth" || (Boolean(o.code) && /[A-Z]/.test(o.code!)),
      )
    : objects;

  // Keep Corel/LO page coordinates 1:1 with the stored SVG underlay.
  // Tight crop is an explicit admin action ("По содержимому"), not default —
  // remapping breaks alignment with the vector underlay.
  return {
    version: 1,
    width: size.width,
    height: size.height,
    rotationDeg: 0,
    objects: cleaned,
    source: {
      format: options?.format ?? "svg",
      fileName: options?.fileName,
      importedAt: new Date().toISOString(),
      boothCodesHint: options?.boothCodesHint,
    },
  };
}

/**
 * Crop empty page margins and remap objects into a tight 0–100 board
 * matching the pavilion content aspect ratio.
 */
export function normalizeFloorplanScene(
  scene: FloorplanScene,
  options?: { paddingPct?: number; maxSide?: number },
): FloorplanScene {
  const pad = Math.min(12, Math.max(1, options?.paddingPct ?? 3));
  const maxSide = options?.maxSide ?? 1400;
  const objs = scene.objects.filter(
    (o) =>
      Number.isFinite(o.x) &&
      Number.isFinite(o.y) &&
      Number.isFinite(o.w) &&
      Number.isFinite(o.h) &&
      o.w > 0.25 &&
      o.h > 0.25,
  );
  if (!objs.length) return scene;

  const core = objs.filter((o) => o.kind === "booth" && o.code && /[A-Z]/.test(o.code));
  // Seed from letter booths + architecture (ВХОД/W/C live in the service corridor).
  const archObjs = objs.filter((o) => o.kind !== "booth");
  const seed = core.length >= 3 ? [...core, ...archObjs] : objs;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const o of seed) {
    minX = Math.min(minX, o.x);
    minY = Math.min(minY, o.y);
    maxX = Math.max(maxX, o.x + o.w);
    maxY = Math.max(maxY, o.y + o.h);
  }
  const spanX = Math.max(1, maxX - minX);
  const spanY = Math.max(1, maxY - minY);
  // Wide margin so corridor entrances / W/C next to the hall are not cropped away.
  const mx = spanX * 0.22;
  const my = spanY * 0.22;
  const keep = objs.filter((o) => {
    if (o.kind !== "booth") return true; // always keep arch markers
    const cx = o.x + o.w / 2;
    const cy = o.y + o.h / 2;
    return cx >= minX - mx && cx <= maxX + mx && cy >= minY - my && cy <= maxY + my;
  });
  for (const o of keep) {
    minX = Math.min(minX, o.x);
    minY = Math.min(minY, o.y);
    maxX = Math.max(maxX, o.x + o.w);
    maxY = Math.max(maxY, o.y + o.h);
  }
  const contentW = Math.max(1, maxX - minX);
  const contentH = Math.max(1, maxY - minY);
  const usable = 100 - pad * 2;
  const scale = Math.min(usable / contentW, usable / contentH);
  const usedW = contentW * scale;
  const usedH = contentH * scale;
  const ox = (100 - usedW) / 2;
  const oy = (100 - usedH) / 2;

  const mapped = keep.map((o) => ({
    ...o,
    x: round2(ox + (o.x - minX) * scale),
    y: round2(oy + (o.y - minY) * scale),
    w: round2(Math.max(0.8, o.w * scale)),
    h: round2(Math.max(0.8, o.h * scale)),
  }));

  let width: number;
  let height: number;
  if (contentW >= contentH) {
    width = maxSide;
    height = Math.max(280, Math.round(maxSide * (contentH / contentW)));
  } else {
    height = maxSide;
    width = Math.max(280, Math.round(maxSide * (contentW / contentH)));
  }

  return {
    ...scene,
    width,
    height,
    objects: mapped,
  };
}

/** Rotate the whole plan by 90° steps (clockwise positive). */
export function rotateFloorplanScene(
  scene: FloorplanScene,
  quarterTurns: 1 | -1 | 2,
): FloorplanScene {
  const turns = ((quarterTurns % 4) + 4) % 4 as 0 | 1 | 2 | 3;
  let objects = scene.objects.map((o) => ({ ...o }));
  let width = scene.width;
  let height = scene.height;
  for (let t = 0; t < turns; t++) {
    objects = objects.map((o) => ({
      ...o,
      x: round2(100 - (o.y + o.h)),
      y: round2(o.x),
      w: o.h,
      h: o.w,
    }));
    const tmp = width;
    width = height;
    height = tmp;
  }
  const prev = scene.rotationDeg ?? 0;
  const next = ((((prev / 90 + turns) % 4) + 4) % 4) * 90 as 0 | 90 | 180 | 270;
  return { ...scene, width, height, objects, rotationDeg: next };
}

export function isFloorplanScene(value: unknown): value is FloorplanScene {
  if (!value || typeof value !== "object") return false;
  const s = value as FloorplanScene;
  return s.version === 1 && Array.isArray(s.objects) && s.width > 0 && s.height > 0;
}

export function sceneBoothRects(scene: FloorplanScene | null | undefined): LabeledBoothRect[] {
  if (!scene) return [];
  return scene.objects
    .filter((o) => o.kind === "booth")
    .map((o) => ({
      x: o.x,
      y: o.y,
      w: o.w,
      h: o.h,
      cx: o.x + o.w / 2,
      cy: o.y + o.h / 2,
      code: o.code ?? "",
    }))
    .filter((o) => o.code);
}

export type FloorplanContentPad = NonNullable<FloorplanScene["contentPad"]>;

export function hasContentPad(pad?: FloorplanContentPad | null): boolean {
  return Boolean(pad && (pad.left || pad.top || pad.right || pad.bottom));
}

/**
 * Map a box from underlay-local % (0–100 of Corel SVG) into board % after
 * «Поле ↑/+10%» (contentPad). Autoplaces / snaps must use this or pins crawl
 * into the empty margin above the inset underlay.
 */
export function mapBoxThroughContentPad<
  T extends { x: number; y: number; w: number; h: number; cx?: number; cy?: number },
>(box: T, pad?: FloorplanContentPad | null): T {
  if (!hasContentPad(pad)) return box;
  const p = pad!;
  const spanX = (100 - p.left - p.right) / 100;
  const spanY = (100 - p.top - p.bottom) / 100;
  const x = p.left + box.x * spanX;
  const y = p.top + box.y * spanY;
  const w = Math.max(0.4, box.w * spanX);
  const h = Math.max(0.4, box.h * spanY);
  const next = {
    ...box,
    x: Number(x.toFixed(2)),
    y: Number(y.toFixed(2)),
    w: Number(w.toFixed(2)),
    h: Number(h.toFixed(2)),
  } as T;
  if (typeof box.cx === "number") {
    (next as { cx?: number }).cx = Number((p.left + box.cx * spanX).toFixed(2));
  }
  if (typeof box.cy === "number") {
    (next as { cy?: number }).cy = Number((p.top + box.cy * spanY).toFixed(2));
  }
  return next;
}

export function mapBoxesThroughContentPad<
  T extends { x: number; y: number; w: number; h: number; cx?: number; cy?: number },
>(boxes: T[], pad?: FloorplanContentPad | null): T[] {
  if (!hasContentPad(pad)) return boxes;
  return boxes.map((b) => mapBoxThroughContentPad(b, pad));
}

export function sceneHasContent(scene: FloorplanScene | null | undefined): boolean {
  return Boolean(scene && scene.objects.length > 0);
}

/** Default fill colors for our schematic (not Corel palette). */
export const FLOORPLAN_KIND_STYLE: Record<
  FloorplanObjectKind,
  { fill: string; stroke: string; text: string }
> = {
  // Close to Corel pavilion cells (lavender fill, dark outline).
  booth: { fill: "#d9d6ef", stroke: "#1e293b", text: "#0f172a" },
  entrance: { fill: "#dcfce7", stroke: "#16a34a", text: "#14532d" },
  exit: { fill: "#fee2e2", stroke: "#dc2626", text: "#7f1d1d" },
  stairs: { fill: "#fef3c7", stroke: "#d97706", text: "#78350f" },
  toilet: { fill: "#e0e7ff", stroke: "#6366f1", text: "#312e81" },
  info: { fill: "#fce7f3", stroke: "#db2777", text: "#831843" },
  service: { fill: "#ffedd5", stroke: "#ea580c", text: "#7c2d12" },
  zone: { fill: "#f1f5f9", stroke: "#cbd5e1", text: "#475569" },
  other: { fill: "#f8fafc", stroke: "#94a3b8", text: "#64748b" },
  event_link: { fill: "#eef2ff", stroke: "#4f46e5", text: "#312e81" },
};
